import React, { useCallback } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useFileUpload } from '../../hooks/useFileUpload';
import { formatFileSize } from '../../utils/formatters';
import { ErrorMessage } from '../common/ErrorMessage';
import { motion } from 'framer-motion';

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  required?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 10,
  required = true,
}) => {
  const { t } = useTranslation();
  const { isRTL, language } = useLanguageStore();
  const { files, previews, error, addFiles, removeFile } = useFileUpload(maxFiles);

  React.useEffect(() => {
    onFilesChange(files);
  }, [files, onFilesChange]);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFiles = Array.from(e.dataTransfer.files);
      addFiles(droppedFiles);
    },
    [addFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      addFiles(selectedFiles);
    },
    [addFiles]
  );

  return (
    <div className="mb-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem', direction: isRTL ? 'rtl' : 'ltr' }}>
        <ImageIcon className="h-4 w-4 text-primary" />
        <span>{t('visits.visitImages', 'صور الزيارة')}</span>
        {required && <span className="text-danger">*</span>}
      </label>

      <motion.div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed rounded-lg p-6 text-center transition-all"
        style={{
          borderColor: files.length > 0 ? '#3b82f6' : '#d1d5db',
          backgroundColor: files.length > 0 ? '#eff6ff' : '#ffffff',
          cursor: 'pointer',
        }}
        whileHover={{ borderColor: '#3b82f6', backgroundColor: '#eff6ff' }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0.7 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Upload className="mx-auto mb-3" size={48} style={{ color: '#9ca3af' }} />
        </motion.div>
        <p className="mb-3 fw-medium" style={{ 
          fontSize: '0.95rem',
          color: '#4b5563',
          fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
        }}>
          {t('visits.dragDropImages', 'اسحب وأفلت الصور هنا، أو انقر للاختيار')}
        </p>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="d-none"
          id="file-upload"
        />
        <label
          htmlFor="file-upload"
          className="btn btn-primary d-inline-flex align-items-center gap-2"
          style={{
            cursor: 'pointer',
            padding: '0.5rem 1.5rem',
            borderRadius: '0.5rem',
            fontWeight: 500,
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        >
          <Upload size={16} />
          {t('visits.selectFiles', 'اختر الصور')}
        </label>
        <p className="text-muted small mt-3 mb-0" style={{ fontSize: '0.875rem' }}>
          {t('visits.maxFiles', 'الحد الأقصى')} {maxFiles} {t('visits.files', 'ملف')}، {t('visits.maxFileSize', '10 ميجابايت لكل ملف')}
        </p>
      </motion.div>

      {error && (
        <div className="mt-2">
          <ErrorMessage message={error} />
        </div>
      )}

      {files.length === 0 && required && (
        <motion.p
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-danger small mt-2 mb-0"
          style={{ fontSize: '0.875rem' }}
        >
          {t('visits.atLeastOneFile', 'يرجى رفع صورة واحدة على الأقل')}
        </motion.p>
      )}

      {previews.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="row g-3 mt-4"
        >
          {previews.map((preview, index) => (
            <div key={index} className="col-6 col-md-4 col-lg-3 position-relative">
              <motion.div
                className="position-relative rounded-lg overflow-hidden shadow-sm"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  className="w-100"
                  style={{
                    height: '150px',
                    objectFit: 'cover',
                    borderRadius: '0.5rem',
                  }}
                />
                <motion.button
                  onClick={() => removeFile(index)}
                  className="position-absolute top-0 end-0 m-2 btn btn-danger btn-sm rounded-circle p-1 d-flex align-items-center justify-content-center"
                  style={{
                    width: '32px',
                    height: '32px',
                    opacity: 0.9,
                  }}
                  whileHover={{ scale: 1.1, opacity: 1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X size={16} />
                </motion.button>
                <div className="position-absolute bottom-0 start-0 end-0 p-2" style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                }}>
                  <p className="text-white small mb-0" style={{ fontSize: '0.75rem' }}>
                    {formatFileSize(files[index].size)}
                  </p>
                </div>
              </motion.div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

